import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  X, Plus, Trash, NoteBlank, Lightning, AddressBook, SquaresFour,
  PaperPlaneTilt, GridNine, Printer, CreditCard, PencilSimple,
} from "@phosphor-icons/react";

/**
 * Right-side ticket panel used across both mobile (full-screen sheet)
 * and desktop (fixed-right aside). Extracted from POSPage.jsx (~256
 * lines inline). All handlers and state live on the parent — this
 * component is purely presentational. State + callback bundle keeps
 * the prop signature manageable.
 */
const TicketPanel = ({
  serverMobile,
  state: {
    currentOrder,
    orderItems,
    selectedTable,
    selectedCustomer, setSelectedCustomer,
    ticketName, setTicketName,
    isQuickOrder,
    subtotal, tax, total, taxRate,
  },
  handlers: {
    setView,
    setTicketOpen,
    setShowAssignTable,
    setShowSplitBill,
    handleCloseTicket,
    handleEditTicketItem,
    handleRemoveItem,
    handleSendToKitchen,
    handleDeleteTicket,
    handlePrintCustomerCopy,
    handleOpenPayment,
  },
}) => {
  const headerTitle = isQuickOrder ? (
    <><Lightning size={serverMobile ? 16 : 18} weight="fill" className="text-accent" /> Quick Order</>
  ) : selectedTable ? selectedTable.name : "New Ticket";

  const subheader = isQuickOrder
    ? (serverMobile && selectedCustomer ? selectedCustomer.name : "Bar / Walk-up")
    : selectedTable ? `${selectedTable.seats} seats` : null;

  const customerBadge = selectedCustomer && (
    <div className="flex items-center gap-1.5 mt-1 bg-primary/5 rounded-md px-2 py-1 w-fit" data-testid="ticket-customer-name">
      <AddressBook size={serverMobile ? 12 : 14} className="text-primary" weight="fill" />
      <span className={`${serverMobile ? "text-xs" : "text-sm"} font-medium text-primary`}>{selectedCustomer.name}</span>
      <button onClick={() => setSelectedCustomer(null)} className="text-primary/50 hover:text-primary ml-1"><X size={serverMobile ? 10 : 12} /></button>
    </div>
  );

  const nameBadge = ticketName && !selectedCustomer && (
    <div className="flex items-center gap-1.5 mt-1 bg-accent/10 rounded-md px-2 py-1 w-fit" data-testid="ticket-name">
      <span className={`${serverMobile ? "text-xs" : "text-sm"} font-medium text-accent-foreground/80`}>{ticketName}</span>
      <button onClick={() => setTicketName("")} className="text-muted-foreground/60 hover:text-foreground ml-1"><X size={serverMobile ? 10 : 12} /></button>
    </div>
  );

  const items = orderItems.length === 0 ? (
    <div className="text-center py-12 text-muted-foreground">
      <NoteBlank size={serverMobile ? 40 : 48} className="mx-auto mb-2 opacity-30" />
      <p className={serverMobile ? "text-sm" : ""}>Empty ticket</p>
      <p className={serverMobile ? "text-xs" : "text-sm"}>
        {serverMobile ? "Tap Menu to add items" : "Add items from the menu"}
      </p>
    </div>
  ) : (
    orderItems.map((item, index) => (
      <div
        key={item.id || index}
        className={`flex items-start justify-between ${serverMobile ? "py-3" : "py-2 group"} border-b border-dashed border-border/50`}
        data-testid={`order-item-${item.id}`}
      >
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">
            <span className="font-mono text-muted-foreground mr-2">{item.quantity}x</span>
            {item.menu_item_name}
          </p>
          {item.modifiers?.length > 0 && (
            <p className={`text-xs text-muted-foreground ml-6 ${serverMobile ? "truncate" : ""}`}>
              {item.modifiers.map((m) => m.name).join(", ")}
            </p>
          )}
          {!serverMobile && item.notes && <p className="text-xs text-primary ml-6 italic">Note: {item.notes}</p>}
        </div>
        <div className={`flex items-center gap-1 ${serverMobile ? "shrink-0 ml-2" : ""}`}>
          <span className="font-mono text-sm">${item.total_price.toFixed(2)}</span>
          {serverMobile ? (
            <>
              <button
                onClick={() => handleEditTicketItem(item)}
                className="text-muted-foreground hover:text-primary p-1"
                data-testid={`edit-item-${item.id}`}
                aria-label="Edit item"
              >
                <PencilSimple size={15} />
              </button>
              <button onClick={() => handleRemoveItem(item.id)} className="text-destructive p-1" data-testid={`remove-item-${item.id}`}>
                <Trash size={16} />
              </button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-primary"
                onClick={() => handleEditTicketItem(item)}
                data-testid={`edit-item-${item.id}`}
                aria-label="Edit item"
              >
                <PencilSimple size={14} />
              </Button>
              <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleRemoveItem(item.id)} data-testid={`remove-item-${item.id}`}>
                <Trash size={14} />
              </Button>
            </>
          )}
        </div>
      </div>
    ))
  );

  const footer = orderItems.length > 0 && (
    <div className={`${serverMobile ? "p-3" : "p-4"} border-t-4 border-t-primary bg-[#FFFDF7] dark:bg-card ${serverMobile ? "safe-area-bottom" : ""}`}>
      <div className={`${serverMobile ? "space-y-1.5" : "space-y-2"} mb-${serverMobile ? "3" : "4"}`}>
        <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">${subtotal.toFixed(2)}</span></div>
        <div className="flex justify-between text-sm"><span className="text-muted-foreground">Tax ({(taxRate * 100).toFixed(1)}%)</span><span className="font-mono">${tax.toFixed(2)}</span></div>
        <Separator />
        <div className="flex justify-between font-medium text-lg"><span>Total</span><span className="font-mono">${total.toFixed(2)}</span></div>
      </div>
      <div className="space-y-2">
        {!selectedTable && !isQuickOrder && !selectedCustomer && !ticketName && (
          <Button
            onClick={() => setShowAssignTable(true)}
            variant="outline"
            className={`w-full gap-2 ${serverMobile ? "h-12 text-base" : ""}`}
            data-testid="assign-table-btn"
          >
            <SquaresFour size={serverMobile ? 18 : 16} /> Assign Ticket
          </Button>
        )}
        {(currentOrder?.status === "pending" || (!currentOrder && orderItems.length > 0)) && (
          <Button
            onClick={handleSendToKitchen}
            className={`w-full gap-2 bg-accent hover:bg-accent/90 ${serverMobile ? "h-12 text-base" : ""}`}
            data-testid="send-to-kitchen-btn"
          >
            <PaperPlaneTilt size={18} weight="fill" /> Send to Kitchen
          </Button>
        )}
        <div className="grid grid-cols-4 gap-2">
          <Button
            variant="outline"
            onClick={handleDeleteTicket}
            className={`gap-1 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30 ${serverMobile ? "h-12 text-base" : ""}`}
            disabled={!currentOrder && orderItems.length === 0 && !selectedTable && !ticketName && !selectedCustomer}
            data-testid="delete-ticket-btn"
          >
            <Trash size={serverMobile ? 18 : 16} /> Delete
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowSplitBill(true)}
            className={`gap-1 ${serverMobile ? "h-12 text-base" : ""}`}
            disabled={orderItems.length === 0}
            data-testid="split-bill-btn"
          >
            <GridNine size={serverMobile ? 18 : 16} /> Split
          </Button>
          <Button
            variant="outline"
            onClick={handlePrintCustomerCopy}
            className={`gap-1 ${serverMobile ? "h-12 text-base" : ""}`}
            disabled={!currentOrder}
            data-testid="print-receipt-btn"
          >
            <Printer size={serverMobile ? 18 : 16} /> Print
          </Button>
          <Button
            onClick={handleOpenPayment}
            className={`gap-1 bg-primary hover:bg-primary/90 ${serverMobile ? "h-12 text-base" : ""}`}
            disabled={orderItems.length === 0}
            data-testid="payment-btn"
          >
            <CreditCard size={serverMobile ? 18 : 16} weight="fill" /> Pay
          </Button>
        </div>
      </div>
    </div>
  );

  if (serverMobile) {
    return (
      <div className="fixed inset-0 z-50 bg-white dark:bg-card flex flex-col" data-testid="ticket-panel">
        {/* Mobile Header */}
        <div className="p-3 border-b border-border flex items-center justify-between bg-white dark:bg-card safe-area-top">
          <div className="min-w-0">
            <h2 className="font-heading font-medium text-base flex items-center gap-2">{headerTitle}</h2>
            {subheader && <p className="text-xs text-muted-foreground">{subheader}</p>}
            {customerBadge}
            {nameBadge}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => { setTicketOpen(false); setView("menu"); }}
              className="gap-1 text-xs px-2 h-8"
              data-testid="mobile-add-items-btn"
            >
              <Plus size={14} /> Add Items
            </Button>
            <Button
              variant="ghost" size="icon"
              onClick={handleCloseTicket}
              className="text-muted-foreground h-8 w-8"
              data-testid="close-ticket-btn"
            >
              <X size={20} />
            </Button>
          </div>
        </div>
        <ScrollArea className="flex-1"><div className="p-3 space-y-1">{items}</div></ScrollArea>
        {footer}
      </div>
    );
  }

  return (
    <aside
      className="fixed right-0 top-0 h-screen w-80 lg:w-96 bg-white dark:bg-card border-l border-border flex flex-col z-40 shadow-lg"
      data-testid="ticket-panel"
    >
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading font-medium text-lg flex items-center gap-2">{headerTitle}</h2>
            {subheader && <p className="text-sm text-muted-foreground">{subheader}</p>}
            {customerBadge}
            {nameBadge}
          </div>
          <Button
            variant="ghost" size="icon"
            onClick={handleCloseTicket}
            className="text-muted-foreground"
            data-testid="close-ticket-btn"
          >
            <X size={20} />
          </Button>
        </div>
      </div>
      <ScrollArea className="flex-1"><div className="p-4 space-y-1">{items}</div></ScrollArea>
      {footer}
    </aside>
  );
};

export default TicketPanel;
